import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ArtPanel from '../components/home/ArtPanel';
import Icon from '../components/ui/Icon';
import Text from '../components/ui/Text';
/* `RATINGS_ARE_INVENTED` lives beside the fabricated numbers themselves -
   one flag, one reason, read by every screen that would otherwise draw them. */
import { Category, RATINGS_ARE_INVENTED, Service } from '../data/services';
import HoursPicker from '../components/service/HoursPicker';
import { cardPrice, cardWasPrice, hourRange, isHourly } from '../data/hourly';
import { detailFor, similarTo } from '../data/serviceDetail';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../theme';

/**
 * Everything about one service, as its own page.
 *
 * The counterpart to the old `ServiceSheet`: same content, but reached by a
 * push rather than raised over whatever was on screen. A service is worth a
 * proper look - the banners, how it works, what is and isn't included, the
 * reviews - and a sheet capped at 93% of the screen made all of that feel
 * like a preview of something else rather than the destination it actually
 * is. Closing it now means going back, not dismissing an overlay.
 */

/** The service banner's width over its height. */
/**
 * How long each banner holds before the next slides in.
 *
 * Four seconds - long enough to take in a photograph, short enough that the
 * second one is seen before the customer scrolls past the picture entirely.
 */
const BANNER_INTERVAL_MS = 4000;


/*
 * `THREAD_DOTS` lived here - five dots forming the thread between two step
 * numbers. It went with the "How it works" band that used it; see the note in
 * the render. Restoring that block means restoring this line:
 *
 *   const THREAD_DOTS = Array.from({ length: 5 }, (_, i) => i);
 */

/**
 * Lines shown in each scope card.
 *
 * Three. The lists run to four and five entries, which made the right column
 * taller than the four steps beside it and pushed the FAQ off the first
 * screen. Three is what fits alongside, and the entries are already ordered
 * most-important first - the templates put diagnosis and labour above
 * consumables, and the excluded parts above the edge cases.
 *
 * What is dropped is not hidden: everything after the third is a detail the
 * FAQ covers, and the full list is what the booking confirmation shows.
 */
const SCOPE_LINES = 3;

const BANNER_ASPECT = 1.86;

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** Reviews shown before "Show more" is offered. */
const REVIEW_PAGE = 5;

/**
 * The ratings and the reviews on this screen are fabricated. They are not
 * shown.
 *
 * ---------------------------------------------------------------------------
 * WHERE THEY CAME FROM
 * ---------------------------------------------------------------------------
 * `Service.rating` and `Service.reviews` are hand-written numbers in
 * `data/services.ts` - 4.78, "860K" - matched to a live service by NAME.
 * `ServiceDetail.breakdown` and `ServiceDetail.reviews` are the same: a star
 * histogram and a list of named customers with review text, all written into
 * `data/serviceDetail.ts` to make the screen look finished.
 *
 * The platform has no ratings. Nothing aggregates them, no endpoint returns
 * them, and no customer wrote a word of any of it.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A CONSTANT AND NOT A DELETION
 * ---------------------------------------------------------------------------
 * It has been harmless only by accident: the match is on name, and not one of
 * the fifteen services the platform actually sells appears in that file, so
 * `rating` is undefined today and these blocks never draw. The day somebody
 * renames a service to "Bathroom Deep Clean", or activates one of the three
 * drafts that ARE in the file, invented stars and invented reviews appear in
 * front of a customer - and nobody will have done anything wrong.
 *
 * So the wire is cut here, in one place, where the reason can be read. When
 * real reviews exist, delete this constant and the three guards that name it;
 * the markup below is intact and will work.
 *
 * NOT to be confused with `data/reviews.ts`, `RatingsScreen` or
 * `ReviewSheet`. Those are the customer's OWN reviews of jobs they booked,
 * they are real, and nothing here touches them.
 */


type Props = {
  service: Service;
  categoryFor: (service: Service) => Category;
  /** How many of this service are already in the cart. */
  quantity: number;
  /**
   * How many items are in the WHOLE cart, not just this service.
   *
   * `quantity` above is this service's own count, and using it alone to decide
   * whether to offer a way to the cart was a trap: add a tap, back out, open a
   * different service, and the footer offered "Add to cart" with no route to
   * the basket you had already filled. The only way through was to go back and
   * find the cart bar on Home.
   *
   * Optional so a screen rendered outside `HomeScreen` - a test, a preview -
   * does not have to invent a number. Absent behaves as it always did.
   */
  cartCount?: number;
  /**
   * The hours picked for this service, on one sold by the hour.
   *
   * Undefined until the customer touches the picker, and on every fixed
   * service - where there is nothing to pick and the price on the card is
   * already the price of the job. The screen defaults it to the service's own
   * minimum rather than to nothing, because a picker with no selection is a
   * question the footer cannot price.
   */
  hours?: number;
  /** Records a change to the picker. Never called for a fixed service. */
  onChangeHours?: (service: Service, hours: number) => void;
  onChangeQuantity: (service: Service, delta: number) => void;
  onBack: () => void;
  /** Go to the cart. */
  onProceed: () => void;
  /** Swap the page over to another service without leaving it. */
  onOpenService: (service: Service) => void;
};

// -------------------------------------------------------------- small parts

function PageSection({
  title,
  children,
  /*
   * Drops the screen gutter, for a section already inside a padded card.
   * Without it the two-column scope cards pay the page's margin twice and
   * their text sits in a column half the width of the card holding it.
   */
  flush = false,
  /*
   * Fills the parent's height, for a section inside a stretched card.
   * The scope cards are equal-height halves; without this the section ends
   * at its own content and anything pinned with an auto margin - the two
   * "View all" links - has no room to be pushed into.
   */
  fill = false,
}: {
  title: string;
  children: React.ReactNode;
  flush?: boolean;
  fill?: boolean;
}) {
  return (
    <View
      style={[
        styles.section,
        flush ? styles.sectionFlush : null,
        fill ? styles.sectionFill : null,
      ]}
    >
      {/* Smaller inside a card: a `title` beside another `title` in the next
          column made two competing headings on one line. */}
      <Text
        variant={flush ? 'subtitle' : 'title'}
        style={styles.sectionTitle}
      >
        {title}
      </Text>
      {children}
    </View>
  );
}

function Bullet({
  icon,
  tint,
  children,
  /*
   * Draws the glyph on a filled disc rather than bare.
   *
   * Only "What's included" uses it. The two scope cards make opposite points
   * and a tick and a dash at the same weight do not say so from across the
   * screen - a solid disc reads as affirmation before the word beside it is
   * read, and leaving "Not included" bare is what makes the contrast work.
   */
  solid = false,
}: {
  /* `leaf` went with "Before your appointment"; the two scope lists are the
     only callers now. Widen this again if a third list appears. */
  icon: 'check' | 'minus';
  tint: string;
  children: React.ReactNode;
  solid?: boolean;
}) {
  return (
    <View style={styles.bullet}>
      {/*
        Both lists get a disc; only the affirmative one is filled.

        "Not included" carries a pale disc rather than none at all, so the two
        columns scan as the same kind of list - a bare glyph beside a filled
        one reads as unfinished rather than as the opposite claim. The contrast
        is carried by the fill, not by the presence of the circle.
      */}
      <View
        style={[
          styles.bulletMark,
          solid ? { backgroundColor: tint } : styles.bulletMarkSoft,
        ]}
      >
        <Icon
          name={icon}
          size={11}
          color={solid ? colors.onPrimary : tint}
          strokeWidth={2.6}
        />
      </View>
      {/* Three lines is the longest any inclusion actually runs; past that a
          single bullet outgrows the card and the list stops scanning. */}
      <Text
        variant="caption"
        color={colors.inkBody}
        style={styles.bulletText}
        numberOfLines={3}
      >
        {children}
      </Text>
    </View>
  );
}

/** One row of the star histogram. */
function RatingBar({ stars, count, total }: { stars: number; count: number; total: number }) {
  const share = total > 0 ? count / total : 0;
  return (
    <View style={styles.barRow}>
      <Text variant="caption" style={styles.barStar}>
        {stars}
      </Text>
      <Icon name="starFilled" size={11} color={colors.rating} />
      <View style={styles.barTrack}>
        <View style={[styles.barFill, { width: `${Math.round(share * 100)}%` }]} />
      </View>
      <Text variant="caption" style={styles.barCount}>
        {count}
      </Text>
    </View>
  );
}

function Stars({ rating, size = 13 }: { rating: number; size?: number }) {
  return (
    <View style={styles.stars}>
      {[1, 2, 3, 4, 5].map(n => (
        <Icon
          key={n}
          name={n <= Math.round(rating) ? 'starFilled' : 'star'}
          size={size}
          color={colors.rating}
          strokeWidth={1.6}
        />
      ))}
    </View>
  );
}

// -------------------------------------------------------------------- page

function ServiceScreen({
  service,
  categoryFor,
  quantity,
  cartCount = 0,
  hours,
  onChangeHours,
  onChangeQuantity,
  onBack,
  onProceed,
  onOpenService,
}: Props) {
  const resp = useResponsive();
  const insets = useSafeAreaInsets();

  /** Which FAQ is expanded, by index. */
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [allReviews, setAllReviews] = useState(false);
  /**
   * Which scope list the sheet is showing, or null when it is closed.
   *
   * One piece of state for both lists rather than a boolean each: the sheet
   * shows one at a time, and two booleans can both be true, which is a state
   * the sheet has no way to render.
   */
  const [scopeSheet, setScopeSheet] = useState<'includes' | 'excludes' | null>(
    null,
  );

  /**
   * The banner carousel, advancing on its own.
   *
   * `bannerRef` scrolls it; `bannerPage` is where it currently is. Both are
   * needed because `scrollTo` is imperative while the page has to survive a
   * manual swipe - the timer picks up from wherever the finger left it rather
   * than from where it thought it was.
   *
   * The page is a ref, not state, and that is deliberate. Nothing on screen
   * renders it - there are no dots and no counter, only the picture, which the
   * pager itself has already moved. As state it re-rendered this entire screen
   * every four seconds, and again on every swipe, to produce no visible change.
   */
  const bannerRef = useRef<ScrollView>(null);
  const bannerPage = useRef(0);
  /* Set while a finger is down, so the timer does not yank the picture out
     from under someone who is looking through them themselves. */
  const bannerHeld = useRef(false);

  /* The category comes along for its photograph - see `bannersFor`. The
     carousel used to top itself up with the app's own bundled artwork, which
     put pictures of somebody else's work beside this service's name. */
  const detail = useMemo(
    () => detailFor(service, categoryFor(service)),
    [service, categoryFor],
  );
  const similar = useMemo(() => similarTo(service), [service]);

  /* Back to the first picture when the service changes - this screen is
     reused for the next service, so page 3 of the last one would persist. */
  useEffect(() => {
    bannerPage.current = 0;
    bannerRef.current?.scrollTo({ x: 0, animated: false });
  }, [service.key]);

  const inCart = quantity > 0;

  /*
   * What this service costs as it is currently configured.
   *
   * On a fixed service it is the catalogue price and nothing here does
   * anything. On an hourly one the catalogue price is the rate for a single
   * hour, so every figure this page shows - the footer button above all - has
   * to be that rate times the hours chosen. Showing the bare price against a
   * three-hour booking would quote a number nobody will be charged.
   *
   * The fallback to the minimum is not a guess about what the customer wants:
   * it is the only starting value that cannot overcharge them, and the picker
   * is right there showing which one is selected.
   */
  const sold = isHourly(service);
  const pickedHours = hours ?? hourRange(service).min;
  const unitPrice = sold ? service.price * pickedHours : service.price;
  const priceLabel = unitPrice === 0 ? 'Free' : rupees(unitPrice);
  /* Edge to edge. The banner used to be inset by the page gutter and rounded,
     which left a band of ground above and beside it; the picture is the first
     thing on the page and should own the top of the screen. */
  const bannerWidth = resp.contentWidth;
  const bannerHeight = Math.round(bannerWidth / BANNER_ASPECT);

  /**
   * Advancing the banners on their own.
   *
   * Only when there is more than one to show, and never while a finger is on
   * the picture - an auto-advance that fires mid-swipe fights the person
   * using it, which is worse than not moving at all.
   *
   * Wraps back to the first rather than stopping at the last: a carousel that
   * quietly halts looks broken, and there is no page indicator here to explain
   * that it reached the end.
   */
  useEffect(() => {
    const count = detail.banners.length;
    if (count < 2 || bannerWidth <= 0) {
      return;
    }

    const timer = setInterval(() => {
      if (bannerHeld.current) {
        return;
      }
      const next = (bannerPage.current + 1) % count;
      bannerPage.current = next;
      bannerRef.current?.scrollTo({ x: next * bannerWidth, animated: true });
    }, BANNER_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [bannerWidth, detail.banners.length]);

  const totalRatings = detail.breakdown.reduce((sum, n) => sum + n, 0);
  const shownReviews = allReviews ? detail.reviews : detail.reviews.slice(0, REVIEW_PAGE);

  return (
    <View style={styles.root}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[styles.scroll, resp.column]}
      >
        {/* ------------------------------------------------------ banners */}
        <View>
          <ScrollView
            ref={bannerRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.banners}
            /* A finger on the picture suspends the timer; letting go both
               resumes it and tells it which page the swipe landed on, so the
               next auto-advance continues from there rather than jumping. */
            onScrollBeginDrag={() => {
              bannerHeld.current = true;
            }}
            onScrollEndDrag={() => {
              bannerHeld.current = false;
            }}
            onMomentumScrollEnd={event => {
              bannerPage.current = bannerWidth
                ? Math.round(event.nativeEvent.contentOffset.x / bannerWidth)
                : 0;
            }}
          >
            {detail.banners.map((banner, i) => (
              <Image
                key={i}
                source={banner}
                style={[
                  styles.banner,
                  { width: bannerWidth, height: bannerHeight },
                ]}
                resizeMode="cover"
              />
            ))}
          </ScrollView>

          {/*
            The name, the blurb and the numbers, over the foot of the picture.

            Below it they cost a block of the first screen before anything
            about the service itself appeared. The photograph already has dead
            ground at its bottom edge - that is where a caption belongs, and it
            buys the whole block back for the columns underneath.

            Outside the pager, not inside it, so the caption stays put while
            the pictures slide under it.
          */}
          <View style={styles.bannerScrim} pointerEvents="none">
            <Text
              variant="title"
              color={colors.onPrimary}
              style={styles.bannerShadow}
            >
              {service.name}
            </Text>
            <Text
              variant="bodySm"
              color={colors.onPrimary}
              style={styles.bannerShadow}
            >
              {service.blurb}
            </Text>

            <View style={styles.bannerMeta}>
              {/*
                Nothing at all, not "No ratings yet".

                That line was the honest version of a rating this app cannot
                produce, and honest was the right instinct - but it is still a
                sentence about an absence, printed over the banner of a service
                somebody is deciding on. It reads as a warning: *other* services
                have ratings and this one does not. Every service is in the same
                position, so the line said nothing true about this one and put a
                doubt under all of them.

                The trailing `·` lives inside the branch below on purpose: it
                separates the rating from the duration, so it has to disappear
                with the thing it separates or the banner opens on a stray dot.

                Flipping `RATINGS_ARE_INVENTED` brings the whole group back.
              */}
              {RATINGS_ARE_INVENTED || service.rating === undefined ? null : (
                <>
                  <Icon name="starFilled" size={13} color={colors.onPrimary} />
                  <Text
                    variant="labelSm"
                    color={colors.onPrimary}
                    style={styles.bannerShadow}
                  >
                    {service.rating.toFixed(2)}
                  </Text>
                  <Text
                    variant="caption"
                    color={colors.onPrimary}
                    style={styles.bannerShadow}
                  >
                    ({service.reviews} reviews)
                  </Text>
                  <Text
                    variant="caption"
                    color={colors.onPrimary}
                    style={styles.bannerShadow}
                  >
                    ·
                  </Text>
                </>
              )}
              <Text
                variant="caption"
                color={colors.onPrimary}
                style={styles.bannerShadow}
              >
                {service.minutes} min
              </Text>
            </View>
          </View>
        </View>

        {/* ---------------------------------------------------- similar */}
        {similar.length > 0 ? (
          <PageSection title="Similar services">
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.similarRail}
            >
              {similar.map(other => (
                <Pressable
                  key={other.key}
                  accessibilityRole="button"
                  accessibilityLabel={`Open ${other.name}`}
                  onPress={() => onOpenService(other)}
                  style={({ pressed }) => [
                    styles.similarCard,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <ArtPanel
                    image={other.image}
                    icon={categoryFor(other).icon}
                    style={styles.similarArt}
                  />
                  <Text variant="labelSm" numberOfLines={2} style={styles.similarName}>
                    {other.name}
                  </Text>
                  <Text variant="caption" color={colors.inkMuted}>
                    {/* Nested so the strike lands on this figure alone. */}
                    {cardWasPrice(other, rupees) ? (
                      <Text style={styles.was}>
                        {cardWasPrice(other, rupees)}{' '}
                      </Text>
                    ) : null}
                    {cardPrice(other, rupees)}
                  </Text>
                </Pressable>
              ))}
            </ScrollView>
          </PageSection>
        ) : null}

        {/*
          ------------------------------------------------ how long

          Above where "How it works" used to sit, because on an hourly service
          this is the decision the rest of the page is describing - how much of
          a Pro's day is being bought - and it is what the footer button is
          priced from. A customer who reaches the button without having seen
          this control has been quoted a number they cannot account for.

          Absent entirely on a fixed service. There is nothing to choose there,
          and a picker permanently stuck on one option is a control that
          teaches people it does nothing.
        */}
        {sold && onChangeHours ? (
          <View style={styles.panel}>
            <HoursPicker
              service={service}
              value={pickedHours}
              onChange={next => onChangeHours(service, next)}
            />
          </View>
        ) : null}

        {/*
          ------------------------------------------------ how it works

          "How it works" used to sit here and is gone.

          It laid the four steps - arrive, inspect, quote, close - across one
          wide band. Removed on request: the sequence is the same for every
          service on the platform, so it repeated on every service page while
          saying nothing specific about the one being read. `detail.steps` is
          still populated and the styles below it are untouched, so restoring
          the band is putting this block back rather than rebuilding it.

          The hours picker above took its place in the layout, which is why
          that block's own note says "above where How it works used to sit".
        */}

        {/* ------------------------------------------ includes / excludes */}
        {/*
          Drawn only where ops has written something.

          `detailFor` no longer falls back to the trade's generic list, so
          either of these can now be empty - and an empty one used to render
          as a heading with a "View all 0" link under it, which reads as a
          service whose page failed to load rather than one nobody has
          described yet.

          Each column is gated on its OWN list, not on the pair: a service can
          easily have inclusions written and exclusions not, and hiding both
          because one is missing would throw away copy that exists. The row
          itself goes only when neither has anything, so a single card sits at
          its own half-width where it always did rather than stretching.
        */}
        {detail.includes.length || detail.excludes.length ? (
        <View style={styles.columns}>
          {detail.includes.length ? (
          <View style={styles.column}>
            <View style={styles.scopeCard}>
              <PageSection title="What's included" flush fill>
                {detail.includes.slice(0, SCOPE_LINES).map(item => (
                  <Bullet key={item} icon="check" tint={colors.success} solid>
                    {item}
                  </Bullet>
                ))}

                {/*
                  On both cards, always - the count says what it opens.

                  This used to render only when the list was longer than the
                  three lines shown, on the argument that opening a dialog
                  identical to the card reads as broken. In practice the two
                  cards rarely hold the same number of lines, so one card got
                  the link and its neighbour did not, and the missing one read
                  as a bug rather than as "there is nothing more here". The
                  dialog is also the readable version of a half-width column,
                  which is worth opening even when it holds the same lines.
                */}
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`View all ${detail.includes.length} inclusions`}
                  onPress={() => setScopeSheet('includes')}
                  style={({ pressed }) => [
                    styles.scopeMore,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <Text variant="labelSm" color={colors.primary}>
                    View all {detail.includes.length}
                  </Text>
                </Pressable>
              </PageSection>
            </View>
          </View>
          ) : null}

          {detail.excludes.length ? (
          <View style={styles.column}>
            <View style={[styles.scopeCard, styles.scopeCardPlain]}>
              <PageSection title="Not included" flush fill>
                {detail.excludes.slice(0, SCOPE_LINES).map(item => (
                  <Bullet key={item} icon="minus" tint={colors.inkSubtle}>
                    {item}
                  </Bullet>
                ))}

                {/* Unconditional, same as its twin above. */}
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`View all ${detail.excludes.length} exclusions`}
                  onPress={() => setScopeSheet('excludes')}
                  style={({ pressed }) => [
                    styles.scopeMore,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <Text variant="labelSm" color={colors.primary}>
                    View all {detail.excludes.length}
                  </Text>
                </Pressable>
              </PageSection>
            </View>
          </View>
          ) : null}
        </View>
        ) : null}


        {/*
          --------------------------------------------------------- faqs

          In a card, like the three above it, so the page reads as a stack of
          panels rather than as two carded columns followed by loose text.

          "Before your appointment" used to sit here and is gone: its tips
          ("have the model number ready", "clear 60cm around the appliance")
          are things to do before the professional arrives, which is after
          booking - on a page whose job is deciding whether to book at all,
          they answered a question nobody had asked yet. `detail.tips` is
          still populated and belongs on the booking confirmation.
        */}
        {/*
          Same rule as the scope cards: ops' questions, or no card.

          "Frequently asked" over nothing is worse than the section being
          absent - it names a thing the page is failing to show. Of the eleven
          live services, "Hourly Services" has no FAQs at all, so this is a
          card that would have been empty on a real page today.
        */}
        {detail.faqs.length ? (
        <View style={styles.panel}>
          <PageSection title="Frequently asked" flush>
            {detail.faqs.map((faq, i) => {
              const open = openFaq === i;
              return (
                <Pressable
                  key={faq.question}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: open }}
                  accessibilityLabel={faq.question}
                  onPress={() => setOpenFaq(open ? null : i)}
                  style={styles.faq}
                >
                  <View style={styles.faqHead}>
                    <Text variant="labelSm" style={styles.faqQuestion}>
                      {faq.question}
                    </Text>
                    <Icon
                      name={open ? 'minus' : 'plus'}
                      size={15}
                      color={colors.inkMuted}
                      strokeWidth={2.2}
                    />
                  </View>
                  {open ? (
                    <Text
                      variant="caption"
                      color={colors.inkMuted}
                      style={styles.faqAnswer}
                    >
                      {faq.answer}
                    </Text>
                  ) : null}
                </Pressable>
              );
            })}
          </PageSection>
        </View>
        ) : null}

        {/* ------------------------------------------------- rating split */}
        {/*
          Both the star histogram and the named reviews below it are written
          into `data/serviceDetail.ts` by hand. See RATINGS_ARE_INVENTED.
        */}
        {RATINGS_ARE_INVENTED || service.rating === undefined ? null : (
          <View style={styles.panel}>
            <PageSection title="Ratings" flush>
              <View style={styles.ratingSummary}>
                <View style={styles.ratingBig}>
                  <Text variant="display">{service.rating.toFixed(1)}</Text>
                  <Stars rating={service.rating} />
                  <Text variant="caption" color={colors.inkMuted}>
                    {service.reviews} ratings
                  </Text>
                </View>

                <View style={styles.bars}>
                  {detail.breakdown.map((count, i) => (
                    <RatingBar
                      key={5 - i}
                      stars={5 - i}
                      count={count}
                      total={totalRatings}
                    />
                  ))}
                </View>
              </View>
            </PageSection>
          </View>
        )}

        {/* ----------------------------------------------------- reviews */}
        {/*
          Every name, star and sentence under this heading was written into
          `data/serviceDetail.ts`. No customer wrote any of it, and it is the
          single most misleading thing this app could show. See
          RATINGS_ARE_INVENTED.
        */}
        {RATINGS_ARE_INVENTED || shownReviews.length === 0 ? null : (
          <View style={styles.panel}>
            <PageSection title="What customers say" flush>
              {shownReviews.map((review, i) => (
                <View key={`${review.name}-${i}`} style={styles.review}>
                  <View style={styles.reviewHead}>
                    <View style={styles.avatar}>
                      <Text variant="labelSm" color={colors.primary}>
                        {review.name.charAt(0)}
                      </Text>
                    </View>
                    <View style={styles.grow}>
                      <Text variant="labelSm">{review.name}</Text>
                      <View style={styles.reviewMeta}>
                        <Stars rating={review.rating} size={11} />
                        <Text variant="caption" color={colors.inkMuted}>
                          {review.when}
                        </Text>
                      </View>
                    </View>
                  </View>
                  <Text
                    variant="caption"
                    color={colors.inkBody}
                    style={styles.reviewBody}
                    numberOfLines={3}
                  >
                    {review.body}
                  </Text>
                </View>
              ))}

              {!allReviews && detail.reviews.length > REVIEW_PAGE ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Show all reviews"
                  onPress={() => setAllReviews(true)}
                  style={({ pressed }) => [
                    styles.showMore,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <Text variant="labelSm" color={colors.primary}>
                    Show all {detail.reviews.length} reviews
                  </Text>
                </Pressable>
              ) : null}
            </PageSection>
          </View>
        )}
      </ScrollView>

      {/*
        Back, outside the ScrollView as the footer is, so it stays over the top
        corner however far down the page somebody has read. It sat on the
        banner and scrolled away with it, leaving a long page with no way off
        on an iPhone but to scroll all the way back up. Offset by the column's
        side inset, which is what the banner under it is centred by.
      */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Back"
        onPress={onBack}
        hitSlop={10}
        style={({ pressed }) => [
          styles.back,
          { left: screenPadding + resp.sideInset },
          pressed ? styles.pressed : null,
        ]}
      >
        <Icon name="chevronLeft" size={20} color={colors.ink} />
      </Pressable>

      {/* Outside the ScrollView, so it stays put at the foot of the page. */}
      {/* Lifted clear of the gesture bar. A fixed `paddingBottom` put the
          button under the system navigation on a phone with a tall inset -
          tappable in theory, covered in practice. `insets.bottom` is 0 on
          hardware-key phones, so the padding below is what it falls back to. */}
      {/*
        The gutter is added to `resp.column`, not set beside it.

        `resp.column` is `{ paddingHorizontal: sideInset }` - 0 on a phone -
        and it came after `styles.footer` in this array, so it overwrote the
        bar's own horizontal padding with zero and the buttons ran to both
        edges. Folding the two into one value is what keeps a tablet's
        centring AND a phone's gutter.
      */}
      <View
        style={[
          styles.footer,
          {
            paddingHorizontal: spacing.xl + resp.sideInset,
            paddingBottom: spacing.md + insets.bottom,
          },
        ]}
      >
        {inCart ? (
          <View style={styles.footerRow}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Add more services"
              onPress={onBack}
              style={({ pressed }) => [
                styles.secondaryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="label" color={colors.primary}>
                Add more
              </Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Proceed to cart"
              onPress={onProceed}
              style={({ pressed }) => [
                styles.primaryButton,
                styles.grow,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="label" color={colors.onPrimary}>
                Proceed to cart · {quantity}
              </Text>
            </Pressable>
          </View>
        ) : cartCount > 0 ? (
          /*
            This service is not in the cart, but something else is.

            Both controls, because both are wanted: adding THIS service, and
            reaching the basket already filled. Before this branch existed the
            footer showed "Add to cart" alone, and someone who had added three
            things and then opened a fourth service had no route to the cart
            from here at all - they had to press back, twice, and find the bar
            on Home. The cart is the thing they were heading for.

            "View cart" is the quiet control: the reason you are on a service
            page is usually to add it, so adding keeps the filled button.
          */
          <View style={styles.footerRow}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`View cart, ${cartCount} ${
                cartCount === 1 ? 'item' : 'items'
              }`}
              onPress={onProceed}
              style={({ pressed }) => [
                styles.secondaryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="label" color={colors.primary}>
                View cart · {cartCount}
              </Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Add ${service.name} to cart`}
              onPress={() => onChangeQuantity(service, 1)}
              style={({ pressed }) => [
                styles.primaryButton,
                styles.grow,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="label" color={colors.onPrimary}>
                Add · {priceLabel}
              </Text>
            </Pressable>
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Add ${service.name} to cart`}
            onPress={() => onChangeQuantity(service, 1)}
            style={({ pressed }) => [
              styles.primaryButton,
              pressed ? styles.pressed : null,
            ]}
          >
            <Text variant="label" color={colors.onPrimary}>
              Add to cart · {priceLabel}
            </Text>
          </Pressable>
        )}
      </View>

      {/*
        The full scope list, as a card in the MIDDLE of the screen.

        It was a bottom sheet, which put the list against the foot of the
        page - half a screen of dimmed page above it and the system bar right
        under it. A centred dialog is read where the eye already is, and both
        lists are short enough that a sheet's drag affordance bought nothing.

        One dialog for both lists rather than two: they are the same card with
        different contents, and a second copy is a second place for the header
        or the close button to drift. The inner `ScrollView` handles a list
        long enough to overflow the card's own height cap.
      */}
      <Modal
        visible={scopeSheet !== null}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setScopeSheet(null)}
      >
        {/* The scrim is the way out, same as the sheet's was. */}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={() => setScopeSheet(null)}
          style={styles.scopeScrim}
        >
          {/*
            Swallows taps on the card itself - without it, a press anywhere
            inside the dialog reaches the scrim behind and closes it, which
            makes scrolling the list impossible.
          */}
          <Pressable
            style={[styles.scopeDialog, { maxWidth: resp.width - spacing.xl }]}
            onPress={() => {}}
          >
            <View style={styles.scopeSheetHead}>
              <Text variant="title" style={styles.grow}>
                {scopeSheet === 'excludes' ? 'Not included' : "What's included"}
              </Text>

              {/* Top-right, as asked. `hitSlop` because the glyph is small and
                  this is the only way out other than the scrim. */}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Close"
                onPress={() => setScopeSheet(null)}
                hitSlop={12}
                style={({ pressed }) => [pressed ? styles.pressed : null]}
              >
                <Icon name="xCircle" size={22} color={colors.inkMuted} />
              </Pressable>
            </View>

            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.scopeSheetList}
              /* Sizes to the list until it is long, then scrolls inside the
                 cap rather than growing the card past the screen. */
              style={styles.scopeDialogList}
            >
              {(scopeSheet === 'excludes'
                ? detail.excludes
                : detail.includes
              ).map(item =>
                scopeSheet === 'excludes' ? (
                  <Bullet key={item} icon="minus" tint={colors.inkSubtle}>
                    {item}
                  </Bullet>
                ) : (
                  <Bullet key={item} icon="check" tint={colors.success} solid>
                    {item}
                  </Bullet>
                ),
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  /* The strike itself. Nothing else in this app crosses text out. */
  was: {
    textDecorationLine: 'line-through',
  },
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroll: {
    paddingBottom: spacing.xxl,
  },

  banners: {
    /* No gutter and no gap: the pager's pages must be exactly the screen's
       width or `pagingEnabled` stops landing on picture boundaries. */
  },
  banner: {},
  /* Reads the caption over the foot of the picture. Dark enough at the bottom
     for white type, clear at the top so the photograph is not dimmed. */
  bannerScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.xxl,
    paddingBottom: spacing.md,
    /*
     * No panel behind the caption.
     *
     * A filled band across the foot of the photograph cropped it into a
     * letterbox and read as a separate bar rather than as part of the picture.
     * The type carries its own contrast instead - see `bannerShadow` - which
     * keeps the whole frame visible.
     */
  },
  /* Legibility without a panel: a soft dark shadow under white type reads on
     a bright sky or a pale wall, which is where an unbacked caption fails. */
  bannerShadow: {
    textShadowColor: 'rgba(0, 0, 0, 0.55)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  bannerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  back: {
    position: 'absolute',
    top: spacing.xl,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    ...hairline,
  },

  /*
   * The two-column band.
   *
   * `stretch` (the default), so the two scope cards are always the SAME
   * height - with `flex-start` the shorter list ended early and the pair read
   * as mismatched panels rather than as two halves of one statement.
   */
  columns: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    marginTop: spacing.md,
  },
  column: {
    /* Equal halves whatever the text does - a long step title must not widen
       its own column and squeeze the scope cards beside it. */
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
    gap: spacing.sm,
  },
  /* Tinted ground, so the pair reads as one statement of scope against the
     plain numbered steps to their left. `flex: 1` fills the stretched column,
     which is what makes the two cards end on the same line. */
  scopeCard: {
    flex: 1,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceAccent,
    paddingVertical: spacing.md,
  },
  /* Plain white. Only "What's included" keeps the tint - it is the one making
     a positive claim, and three tinted cards in a row said nothing at all. */
  scopeCardPlain: {
    backgroundColor: colors.surface,
  },
  /*
   * A full-width panel, for the sections below the two columns.
   *
   * Same card as the columns above but spanning the page: FAQs, the rating
   * histogram and the review list all carry long lines, and splitting any of
   * them across half a screen would wrap nearly every one.
   */
  panel: {
    marginHorizontal: screenPadding,
    marginTop: spacing.md,
    paddingVertical: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
  },
  section: {
    paddingHorizontal: screenPadding,
    marginTop: spacing.md,
  },
  /* Inside a card that already pads itself. */
  sectionFlush: {
    paddingHorizontal: spacing.md,
    marginTop: 0,
  },
  /* Fills a stretched card, so an auto margin inside has height to consume. */
  sectionFill: {
    flex: 1,
  },
  sectionTitle: {
    marginBottom: spacing.sm,
  },

  similarRail: {
    gap: spacing.md,
    paddingRight: screenPadding,
  },
  similarCard: {
    width: 132,
  },
  similarArt: {
    width: '100%',
    aspectRatio: 1.45,
    borderRadius: radii.md,
  },
  similarName: {
    marginTop: spacing.sm,
  },

  /* Horizontal now, and pinned level with the centre of the numbered discs
     rather than filling the cell's height. */
  /*
   * The dotted run between one step and the next.
   *
   * `flex` rather than a fixed width: the gap between two cells is whatever
   * the row has left over, and a 12pt box in a 40pt gap left the dots huddled
   * in the middle with clear air either side - which read as a dash, not a
   * thread. Given the space it fills it, so the dots reach from one mark to
   * the next.
   */
  stepThread: {
    flex: 1,
    flexBasis: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    /* Half the disc's height, so the run sits on its centreline. */
    marginTop: 13,
  },
  stepDot: {
    width: 2,
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.borderStrong,
  },
  /* 26pt, up from 20 - the numbers are the spine of the diagram and at 20pt
     they read as bullet dots. The thread's offset above follows the centre. */
  stepNumber: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  /*
   * The steps, read across instead of down.
   *
   * `align-items: flex-start` so a three-line title does not stretch the
   * discs beside it out of line - every number stays on the same baseline
   * whatever its caption does underneath.
   */
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stepCell: {
    /*
     * Five parts to the thread's one.
     *
     * Sharing the row equally gave each of the seven children ~52dp, far too
     * narrow for these titles. At 5:1 a cell gets ~80dp and the run between
     * two of them ~16dp - still enough dots to read as a line, and the width
     * goes where the words are.
     */
    flex: 5,
    flexBasis: 0,
    minWidth: 0,
    alignItems: 'center',
    gap: spacing.xs,
  },
  stepTitle: {
    textAlign: 'center',
    /*
     * 10pt - up from the 9 the design review flagged as unreadably small.
     *
     * The ceiling is one word: "professional" is 12 characters and renders
     * ~79dp at 10pt, which is what the widened 5:1 cell above (~80dp) was
     * sized for. At the caption's own 11pt it is ~87dp and clips mid-word,
     * so any further size increase needs the cell ratio revisited with it.
     */
    fontSize: 10,
    lineHeight: 14,
  },

  bullet: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  /* The disc behind the glyph. Transparent unless `solid`, so an unfilled
     mark still occupies the same width and the two lists stay aligned. */
  bulletMark: {
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* The unfilled disc. Tinted ground rather than white, so it still reads as
     a disc on the white card behind it. */
  bulletMarkSoft: {
    backgroundColor: colors.surfaceMuted,
  },
  bulletText: {
    flex: 1,
  },

  faq: {
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  faqHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  faqQuestion: {
    flex: 1,
  },
  faqAnswer: {
    marginTop: spacing.sm,
  },

  ratingSummary: {
    flexDirection: 'row',
    gap: spacing.xl,
    alignItems: 'center',
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  ratingBig: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  stars: {
    flexDirection: 'row',
    gap: 1,
  },
  bars: {
    flex: 1,
    gap: spacing.xs,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  barStar: {
    width: 10,
    textAlign: 'right',
  },
  barTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.surfaceMuted,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: colors.primary,
  },
  barCount: {
    width: 34,
    textAlign: 'right',
  },

  review: {
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  reviewHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  reviewMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: 2,
  },
  reviewBody: {
    marginTop: spacing.sm,
  },
  showMore: {
    alignItems: 'center',
    paddingVertical: spacing.md,
    marginTop: spacing.sm,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.borderStrong,
  },

  /* `spacing.lg`, not the 12pt page gutter - the two buttons run the full
     row, and at 12 the filled one read as touching the screen edge. */
  footer: {
    /* No `paddingHorizontal` here - it is set inline, combined with the
       tablet inset, because a bare `resp.column` in the same style array
       would overwrite whatever this set. See the note at the call site. */
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  footerRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  grow: {
    flex: 1,
  },
  /* The "View all" under a scope card. Left-aligned with the bullets above it
     rather than centred, so it reads as the next line of the list. The auto
     margin pins it to the card's bottom edge - the two cards are stretched to
     the same height, so without it the link under the shorter list floated
     mid-card while its twin sat a bullet lower. */
  scopeMore: {
    marginTop: 'auto',
    paddingTop: spacing.xs,
    alignSelf: 'flex-start',
  },
  /* Title left, close right - the sheet's own header row. */
  /* The dim behind the centred dialog, and what centres it. */
  scopeScrim: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    /* The same dim `BottomSheet` puts behind itself, so a dialog and a sheet
       darken the page by the same amount. */
    backgroundColor: colors.overlay,
  },
  scopeDialog: {
    width: '100%',
    /* Never taller than two-thirds of the screen; the list scrolls inside. */
    maxHeight: '66%',
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.md,
  },
  /* `flexGrow: 0` so a short list draws a short card rather than one padded
     out to the height cap. */
  scopeDialogList: {
    flexGrow: 0,
  },
  scopeSheetHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingBottom: spacing.md,
  },
  scopeSheetList: {
    gap: spacing.xs,
    paddingBottom: spacing.md,
  },
  primaryButton: {
    height: 52,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.md,
    backgroundColor: colors.primary,
  },
  secondaryButton: {
    height: 52,
    paddingHorizontal: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    backgroundColor: colors.surface,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default ServiceScreen;
